import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { submissionAPI, candidateAPI, clientAPI, requirementAPI, emailAPI } from '../utils/api';
import usePersistentState from '../utils/usePersistentState';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Spinner, Button, Badge, EmptyState, Input, Select, Modal, SearchableSelect, Pagination, ConfirmModal } from '../components/UI';
import RichTextEditor from '../components/RichTextEditor';
import SafeHtml from '../components/SafeHtml';
import HistoryPanel from '../components/HistoryPanel';
import GlobalHistoryPanel from '../components/GlobalHistoryPanel';
import CandidateDetailsModal from '../components/CandidateDetailsModal';
import EmailTagInput from '../components/EmailTagInput';
import InterviewerBusyWarning, { busyTargetOf } from '../components/InterviewerBusyWarning';
import TimePicker12 from '../components/TimePicker12';
import { fmtExp, fmtLPA, fmtTimeRange, timeToMinutes, addMinutesToTime } from '../utils/format';
import { authorOptions } from '../utils/authorOptions';
import RoundRsvp from '../components/RoundRsvp';
import ResumeButton from '../components/ResumeButton';
import InterviewRoundModal from '../components/InterviewRoundModal';

const STATUS_OPTIONS = ['Applied', 'Screening', 'Shortlisted', 'Interview', 'Hold', 'Dropped', 'Rejected', 'Hired'];
const STATUS_VARIANT = { Applied: 'default', Screening: 'purple', Shortlisted: 'warning', Interview: 'info', Hold: 'amber', Dropped: 'indigo', Rejected: 'danger', Hired: 'success' };
const MODE_OPTIONS   = ['Online', 'In-Person', 'Phone'];
const CURRENCIES     = ['USD', 'INR', 'EUR', 'GBP', 'AED', 'SGD', 'AUD', 'CAD', 'JPY'];
const BILLING_TYPES  = ['Fixed', 'Hourly', 'Monthly', 'Percentage'];
const BLANK_ROUND    = { date: '', time: '', endTime: '', mode: '', notes: '', title: '', resultDesc: '' };

// ── Interview slot helpers ────────────────────────────────────────────────────
// Default length pre-filled into End Time when a start time is picked.
const DEFAULT_SLOT_MINS = 30;

/** Apply a new start time to a round, keeping the end time coherent: fill it
 *  when blank, push it forward when the new start lands on/after it. An end the
 *  recruiter deliberately set longer/shorter is preserved. Clearing the start
 *  clears the end. */
const withStartTime = (round, time) => {
  const end = round?.endTime || '';
  const sm = timeToMinutes(time);
  const em = timeToMinutes(end);
  const keepEnd = sm != null && em != null && em > sm;
  return { ...round, time, endTime: keepEnd ? end : (addMinutesToTime(time, DEFAULT_SLOT_MINS) || '') };
};

/** Validation message for a round's slot, or null when it's fine. End time is
 *  mandatory once a start is picked — it drives the calendar invite and Teams
 *  meeting length. A date-only round stays valid (it becomes an all-day event). */
const slotError = (round, label) => {
  if (!round?.time) return null;
  if (!round.endTime) return `${label}: pick an end time for the interview.`;
  const sm = timeToMinutes(round.time);
  const em = timeToMinutes(round.endTime);
  if (sm == null || em == null || em <= sm) return `${label}: end time must be after the start time.`;
  return null;
};

/** True when a failed interview/cancel email request may still have been
 *  delivered: no response at all (browser timeout / dropped connection), a 504,
 *  or any error that is not the app's own JSON (a 502/503 page from
 *  openresty/Traefik). The proxy cuts requests at 60 s while the backend keeps
 *  sending, so "please try again" there would deliver a duplicate invite. The
 *  app's own JSON errors (including its 502 "could not be delivered") are real
 *  failures. */
const emailOutcomeUnknown = (err) => {
  const res = err?.response;
  if (!res || res.status === 504) return true;
  return !(res.data && typeof res.data === 'object' && typeof res.data.success === 'boolean');
};

// ── Interviewer prefill helpers ───────────────────────────────────────────────
// A round carries `interviewerEmails` (stamped server-side when its invite was
// sent). Both helpers return the comma-joined form EmailTagInput expects.

/** The interviewers actually invited to THIS round. */
const roundInterviewers = (round) =>
  (Array.isArray(round?.interviewerEmails) ? round.interviewerEmails : []).join(',');

/** Newest round carrying an interviewer list — the fallback seed when the round
 *  in hand has none (a brand-new round inherits the last panel used). */
const lastInterviewers = (rounds = []) =>
  roundInterviewers([...rounds].reverse().find(r => Array.isArray(r.interviewerEmails) && r.interviewerEmails.length));

// Custom status picker that renders each option as a colored Badge in the
// dropdown panel. The panel is rendered via React portal so it escapes the
// surrounding modal's overflow:hidden / overflow:auto clipping — without
// the portal the dropdown gets cut off (visible bug seen in the Update
// Status modal where only the first 1-2 options were reachable).
function StatusPicker({ value, onChange, options, variants, label }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const panelRef   = useRef(null);

  // Recalculate panel position from the trigger's bounding rect. Runs on
  // open and on scroll/resize so the panel stays anchored if the modal scrolls.
  const updateCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const maxLeft = typeof window !== 'undefined'
      ? Math.max(8, window.innerWidth - r.width - 8)
      : r.left;
    setCoords({ top: r.bottom + 4, left: Math.min(r.left, maxLeft), width: r.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updateCoords();
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [open, updateCoords]);

  // Outside click closes — check both trigger and panel.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target))   return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div style={{ position: 'relative' }}>
      {label && (
        <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>{label}</label>
      )}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        className="input-premium w-full rounded-lg px-3 py-2.5 text-sm focus:outline-none"
        style={{
          background: 'var(--input-bg)', color: 'var(--text-primary)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          cursor: 'pointer', border: '1px solid var(--border-subtle)',
        }}
      >
        <span>{value ? <Badge variant={variants[value] || 'default'}>{value}</Badge> : <span style={{ color: 'var(--text-muted)' }}>Select a status…</span>}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: coords.top, left: coords.left, width: coords.width,
            // Modal uses z-index 99999; we need to sit above it.
            zIndex: 1000000,
            background: 'var(--surface-1, #ffffff)',
            border: '1px solid var(--border-muted)',
            borderRadius: 10,
            boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            maxHeight: 320,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', background: opt === value ? 'var(--hover-bg)' : 'transparent',
                border: 'none', cursor: 'pointer', textAlign: 'left',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
              onMouseLeave={e => e.currentTarget.style.background = opt === value ? 'var(--hover-bg)' : 'transparent'}
            >
              <Badge variant={variants[opt] || 'default'}>{opt}</Badge>
              {opt === value && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" style={{ marginLeft: 'auto', color: '#10b981' }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// fmtExp / fmtCTC now live in ../utils/format (shared with the interview tile).

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// "Submitted on" stamp — date AND time, because two submissions on the same day
// is the normal case and the time is what tells you which came first.
// e.g. "5 Aug 2026, 4:12 pm". Null-safe so a legacy row can't blank the card.
function formatDateTime(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return null;
  return dt.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

// Today as YYYY-MM-DD in the USER'S LOCAL timezone — deliberately not
// toISOString().slice(0,10), which returns the UTC date. In IST (UTC+5:30) that
// is a day behind between midnight and 05:30, so an early-morning user would be
// blocked from picking the current day.
const todayLocal = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// Relative age for at-a-glance scanning ("2h ago", "3d ago").
function timeAgo(d) {
  if (!d) return null;
  const ms = Date.now() - new Date(d).getTime();
  if (isNaN(ms) || ms < 0) return 'just now';
  const mins = Math.floor(ms / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return months < 12 ? `${months}mo ago` : `${Math.floor(months / 12)}y ago`;
}
const BLANK_STATUS_MODAL = {
  open: false, submission: null, status: '',
  rejectionReason: '',
  holdReason: '',
  droppedReason: '',
  interviewRounds: [], addingRound: false, newRound: { ...BLANK_ROUND },
  // Index of the round currently being inline-edited via the Reschedule button.
  // null = no round is being rescheduled.
  reschedulingIdx: null,
  // Snapshot of each pre-existing round's original slot (date/time/mode/title,
  // keyed by _origIdx), taken when the modal opens — lets us tell at save time
  // whether the emailed round's slot changed (a reschedule) and cancel a deleted
  // round on the candidate's calendar.
  origSnapshot: [],
  // Scheduled rounds the recruiter deleted this session — emailed as cancellations
  // (with an iCalendar CANCEL) on Save.
  cancelledRounds: [],
  sendEmail: false, toEmail: '', toName: '', attachResume: false,
  // Set by the per-round "Send invite" button when a round was scheduled but
  // never emailed. Keyed by the round's stable _origIdx (not its array index) so
  // deleting another round cannot silently retarget the invite.
  sendInviteOrigIdx: null,
  // Comma-separated interviewer email(s) — the invitation/reschedule/cancel
  // emails also go to them. Mandatory when the email section is active.
  interviewerEmails: '',
  // Optional rich-text note added to THIS invitation only — not stored on the
  // round, so it never silently reappears on a later reschedule.
  customMessage: '',
};

const BLANK_NEW = { candidateId: '', clientId: '', requirementId: '', notes: '', overrideBillingCost: '', overrideBillingCurrency: 'USD', overrideBillingType: '' };


export default function Submissions() {
  const { hasRole, isDeveloper } = useAuth();
  const { theme }   = useTheme();
  const isDark      = theme === 'dark';
  const navigate    = useNavigate();

  const canCreate      = hasRole(['SuperAdmin', 'RecruiterManager', 'Recruiter', 'Developer']);
  const canDelete      = hasRole(['SuperAdmin']);
  const canEditBilling = hasRole(['SuperAdmin']);

  const [submissions,   setSubmissions]   = useState([]);
  const [total,         setTotal]         = useState(0);
  const [page,          setPage]          = useState(1);
  const [pages,         setPages]         = useState(0);
  const [loading,       setLoading]       = useState(true);
  // Search and filters are kept for this tab (survive leaving the page).
  const [search,        setSearch]        = usePersistentState('submissions:search', '');
  // requirementStatus cascades into the Requirement dropdown — selecting
  // Open / On Hold / Closed narrows requirement options and filters the
  // submission list by their requirement's status.
  const [filters,       setFilters]       = usePersistentState('submissions:filters', {
    status: '', clientId: '', requirementId: '', requirementStatus: '', recruiterId: '',
    // Submitted-on range (YYYY-MM-DD; `createdTo` is inclusive server-side) and
    // ordering. Newest-first stays the default — it was the previous behaviour.
    createdFrom: '', createdTo: '', sortOrder: 'DESC',
  });
  // "Recruiter" filter — only SuperAdmin and RecruiterManager see the control.
  const canFilterByRecruiter = hasRole(['SuperAdmin', 'RecruiterManager']);
  const [authors,       setAuthors]       = useState([]);
  const [clients,       setClients]       = useState([]);
  const [requirements,  setRequirements]  = useState([]);
  const [candidates,    setCandidates]    = useState([]);
  const [loadingReqs,   setLoadingReqs]   = useState(false);

  const [statusModal,   setStatusModal]   = useState(BLANK_STATUS_MODAL);
  // Other interviews the chosen interviewer(s) already have in this slot, and
  // whether the recruiter chose to send anyway (asked once per save attempt).
  const [busyConflicts, setBusyConflicts] = useState([]);
  const [busyConfirm,   setBusyConfirm]   = useState(false);
  const busyAckRef = useRef(false);
  const busyTarget = busyTargetOf(statusModal);
  // { round, sub } — the round whose full detail dialog is open.
  const [roundModal,    setRoundModal]    = useState(null);
  // Pending "cancel this interview round" confirmation — { origIdx, snap, when }
  // while the in-app ConfirmModal is open, null otherwise.
  const [cancelConfirm, setCancelConfirm] = useState(null);
  // Requirement-level rule: force the interview invitation email + lock the
  // candidate's name/email.
  const lockEmail = !!statusModal.submission?.requirement?.forceInterviewEmail;
  // Pending confirmation for the "send invitation email" toggle — { next } while
  // the dialog is open, null otherwise.
  const [emailConfirm,  setEmailConfirm]  = useState(null);
  // Interviewer type-ahead source (see StatusUpdateModal for the shared rationale).
  const [interviewerSuggestions, setInterviewerSuggestions] = useState([]);
  useEffect(() => {
    submissionAPI.interviewerSuggestions()
      .then(({ data }) => setInterviewerSuggestions((data.emails || []).map(e => e.email)))
      .catch(() => setInterviewerSuggestions([]));
  }, []);

  // Toggling the invitation email always confirms when switching it ON (it puts
  // real mail in the candidate's and interviewers' inboxes), and when switching
  // it OFF against a requirement that mandates it. Turning off an optional email
  // needs no ceremony.
  const requestSendEmailToggle = (next) => {
    if (!next && !lockEmail) return setStatusModal(m => ({ ...m, sendEmail: false }));
    setEmailConfirm({ next });
  };
  const [newModal,      setNewModal]      = useState(false);
  const [newForm,       setNewForm]       = useState(BLANK_NEW);
  const [editBilling,   setEditBilling]   = useState(null); // { id, overrideBillingCost, overrideBillingCurrency, overrideBillingType, notes }
  const [saving,        setSaving]        = useState(false);
  const [historyPanel,  setHistoryPanel]  = useState({ open: false, id: null, name: '' });
  const [globalHistory, setGlobalHistory] = useState(false);
  // Which submission's candidate-details popup is open (null = none). Lifted to
  // the page level because tiles are rendered inline in a map (can't hold
  // per-tile hooks).
  const [detailsSub,    setDetailsSub]    = useState(null);

  // silent: a background refresh (tab regained focus) swaps the data in place.
  // It must NOT show the spinner — that unmounts the cards, closing any open
  // recording or dialog inside them.
  const load = useCallback(async (p = page, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = { page: p, limit: 15, ...filters };
      if (search.trim()) params.search = search.trim();
      const { data } = await submissionAPI.getAll(params);
      setSubmissions(data.submissions || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPages(data.pages || 0);
      // Filter options ride along with the list, so they always describe the
      // data actually on screen — including authors who are SuperAdmins or have
      // since been deactivated.
      setAuthors(data.authors || []);
    } catch { if (!silent) toast.error('Failed to load submissions'); }
    finally  { if (!silent) setLoading(false); }
  }, [page, filters, search]); // eslint-disable-line

  useEffect(() => { load(1); }, [filters]);

  // Refresh when the tab regains focus — RSVPs land while you are reading the
  // reply in your mail client, so coming back here is exactly when the data is
  // stale. Rate-limited above the 30s response cache; below it the refetch
  // would be served from cache and change nothing. Reloads the CURRENT page so
  // pagination and filters are preserved.
  useEffect(() => {
    let last = Date.now();
    const onFocus = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - last < 31_000) return;
      last = Date.now();
      load(page, { silent: true });
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [load]);

  useEffect(() => {
    clientAPI.dropdown().then(({ data }) => setClients(data.clients || [])).catch(() => {});
    candidateAPI.dropdown().then(({ data }) => setCandidates(data.candidates || [])).catch(() => {});
    // Load all requirements upfront (with client info) for the new submission dropdown
    requirementAPI.dropdown().then(({ data }) => setRequirements(data.requirements || [])).catch(() => {});
  }, []);

  const handleSearch = (e) => { e.preventDefault(); load(1); };

  const openStatus = (sub) => {
    // Migrate old single-round format into interviewRounds array
    const baseRounds = Array.isArray(sub.interviewRounds) && sub.interviewRounds.length > 0
      ? sub.interviewRounds
      : (sub.interviewDate ? [{
          round: 1, date: sub.interviewDate, time: sub.interviewTime || '',
          endTime: sub.interviewEndTime || '',
          mode: sub.interviewMode || '', notes: sub.interviewNotes || '', result: 'Pending',
        }] : []);
    // Tag each existing round with a stable original index (preserved across
    // in-place edits and delete-renumbering since every handler spreads {...rr});
    // newly added rounds get no tag. origSnapshot records each tag's pre-edit slot
    // so a reschedule is detected reliably — including legacy records and after
    // other rounds are deleted.
    const rounds = baseRounds.map((r, i) => ({ ...r, _origIdx: i }));
    const origSnapshot = rounds.map(r => ({
      // The round's stable server id (absent on legacy rounds) — sent as roundId
      // when this round is cancelled, so the CANCEL hits the right calendar event
      // even after renumbering.
      id: r.id,
      // The UID scheme its invites went out under (e.g. legacy 'r<N>'). The PUT
      // has already removed the round when cancel runs, so only this lets the
      // CANCEL reach the event already on the calendars.
      calendarUidKey: r.calendarUidKey,
      origIdx: r._origIdx, round: r.round, date: r.date || '',
      time: r.time || '', endTime: r.endTime || '', mode: r.mode || '', title: r.title || '',
      // Signals the candidate was actually invited to this round — set only when
      // an invite email was sent (inviteSentAt) or a Teams meeting was minted
      // (meetingLink). Gates cancellation so we never "cancel" an uninvited round.
      inviteSentAt: r.inviteSentAt || '', meetingLink: r.meetingLink || '',
      // The interviewers who were actually invited to THIS round — cancellations
      // must notify them (not whatever the chip field currently holds).
      interviewerEmails: Array.isArray(r.interviewerEmails) ? r.interviewerEmails : [],
    }));
    setStatusModal({
      ...BLANK_STATUS_MODAL, open: true, submission: sub,
      status: sub.status,
      rejectionReason: sub.rejectionReason || '',
      holdReason:      sub.holdReason      || '',
      droppedReason:   sub.droppedReason   || '',
      interviewRounds: rounds,
      origSnapshot,
      addingRound: rounds.length === 0, // auto-open form when no rounds yet
      // The requirement can force the interview invitation email on (and lock it).
      sendEmail:    !!sub.requirement?.forceInterviewEmail,
      // The resume is attached by default for every interview email; the
      // requirement additionally LOCKS it on so it can't be unticked.
      attachResume: true,
      toEmail: sub.candidate?.email || '', toName: sub.candidate?.fullName || '',
      // Prefill interviewer emails saved on a previous send for this submission.
      interviewerEmails: lastInterviewers(rounds),
    });
  };

  // Remove a round from the list (renumbering survivors); optionally queue it for
  // a cancellation email on Save. Re-finds the row by its stable _origIdx so it is
  // safe even if indices shifted while the confirm dialog was open.
  const removeRoundByOrigIdx = (origIdx, queueSnap) => {
    setStatusModal(m => {
      const i = m.interviewRounds.findIndex(r => r._origIdx === origIdx);
      if (i < 0) return m;
      const updated = m.interviewRounds.filter((_, k) => k !== i).map((rr, k) => ({ ...rr, round: k + 1 }));
      return {
        ...m,
        interviewRounds: updated,
        cancelledRounds: queueSnap ? [...(m.cancelledRounds || []), queueSnap] : (m.cancelledRounds || []),
        // Close the inline reschedule form if it was on the deleted round; shift it
        // down one slot if it was anchored below the deletion (indices moved up).
        reschedulingIdx: m.reschedulingIdx === i ? null
          : (m.reschedulingIdx > i ? m.reschedulingIdx - 1 : m.reschedulingIdx),
      };
    });
  };

  const handleStatusSave = async () => {
    const busyAcknowledged = busyAckRef.current;
    busyAckRef.current = false;
    // Past-date validation removed — recruiters may log interviews after
    // the fact (forgot to enter a completed/missed interview at the time).

    // Hold requires a reason. holdReason is HTML from RichTextEditor — strip
    // tags before checking so an "empty" editor (which renders as <p><br></p>)
    // is treated as empty.
    if (statusModal.status === 'Hold') {
      const plain = String(statusModal.holdReason || '').replace(/<[^>]*>/g, '').trim();
      if (!plain) {
        return toast.error('Please provide a reason for putting this submission on hold.');
      }
    }

    // Dropped requires a reason (candidate withdrew). Same HTML-strip check.
    if (statusModal.status === 'Dropped') {
      const plain = String(statusModal.droppedReason || '').replace(/<[^>]*>/g, '').trim();
      if (!plain) {
        return toast.error('Please provide a reason the candidate dropped out.');
      }
    }

    // A brand-new round is being scheduled this save (vs. just editing an existing
    // round's result/notes). The invitation email fires ONLY for a newly scheduled
    // round or a reschedule — never for a plain Pass/Fail/notes change.
    const addedNewRound = statusModal.status === 'Interview' && statusModal.addingRound && !!statusModal.newRound.date;
    // A new-round form with content but NO date would be silently discarded on
    // save (the "Date *" label promises otherwise) — block instead of losing it.
    const newRoundDirty = ['title', 'time', 'mode', 'notes'].some(
      k => String(statusModal.newRound?.[k] || '').replace(/<[^>]*>/g, '').trim()
    );
    if (statusModal.status === 'Interview' && statusModal.addingRound && !statusModal.newRound.date && newRoundDirty) {
      return toast.error('Select a date for the new interview round (or clear the form).');
    }
    // End time is required on the round the recruiter is actually scheduling or
    // rescheduling. Deliberately NOT applied to every round — a plain Pass/Fail
    // save on a round that predates end times must not be blocked.
    if (statusModal.status === 'Interview') {
      const editing = [];
      if (statusModal.addingRound && statusModal.newRound.date) {
        editing.push([statusModal.newRound, `Round ${statusModal.interviewRounds.length + 1}`]);
      }
      const rIdx = statusModal.reschedulingIdx;
      if (rIdx != null && statusModal.interviewRounds[rIdx]) {
        const rr = statusModal.interviewRounds[rIdx];
        editing.push([rr, `Round ${rr.round || rIdx + 1}`]);
      }
      for (const [round, label] of editing) {
        const err = slotError(round, label);
        if (err) return toast.error(err);
      }
    }
    let finalRounds = statusModal.interviewRounds;
    if (addedNewRound) {
      finalRounds = [
        ...statusModal.interviewRounds,
        { ...statusModal.newRound, round: statusModal.interviewRounds.length + 1, result: 'Pending' },
      ];
    }
    const wantsOrigIdx = statusModal.sendInviteOrigIdx;
    const explicitIdx  = wantsOrigIdx == null ? -1 : finalRounds.findIndex(r => r._origIdx === wantsOrigIdx);
    const explicitSend = wantsOrigIdx != null && explicitIdx !== -1;
    // Normally the newest round; the round picked via "Send invite" when the
    // recruiter is sending an invitation they forgot. A target deleted before
    // saving resolves to -1 and simply cancels the send.
    const latestRound  = explicitSend ? finalRounds[explicitIdx] : finalRounds[finalRounds.length - 1];
    // Reschedule = the emailed round already existed and its date/time changed →
    // the email leads with a "rescheduled from → to" note. Compare the emailed
    // round against its own pre-edit slot via the stable _origIdx snapshot (robust
    // for legacy records and after other rounds are deleted/renumbered). A newly
    // added round has no _origIdx → no snapshot match → correctly not a reschedule.
    const prevSlot = (statusModal.origSnapshot || []).find(s => s.origIdx === latestRound?._origIdx);
    // An end-time-only change still moves the calendar event's end, so it counts
    // as a reschedule and re-sends the invite.
    const isReschedule = !!(prevSlot && latestRound?.date
      && (prevSlot.date !== latestRound.date
          || (prevSlot.time    || '') !== (latestRound.time    || '')
          || (prevSlot.endTime || '') !== (latestRound.endTime || '')));
    const toCancel = statusModal.cancelledRounds || [];
    const interviewerList = String(statusModal.interviewerEmails || '').split(',').map(s => s.trim()).filter(Boolean);
    // Interviewer email(s) are mandatory whenever this save will send an interview
    // INVITE or RESCHEDULE — they receive the same email as the candidate.
    // (Cancellations are exempt: they notify the interviewers stored on the deleted
    // round itself, so a delete-only save never demands the chip field.)
    const willSendInterviewEmail = statusModal.status === 'Interview' && statusModal.sendEmail && statusModal.toEmail
      // explicitSend is the "I forgot to send it" path: the round is unchanged so
      // neither addedNewRound nor isReschedule holds, and without it the tick box
      // silently did nothing for an already-scheduled round.
      && latestRound?.date && (addedNewRound || isReschedule || explicitSend);
    if (willSendInterviewEmail && interviewerList.length === 0) {
      return toast.error('Add at least one interviewer email — they receive the interview email too.');
    }
    // An interviewer already booked for another interview in this slot: ask
    // before double-booking them (the warning is already shown in the form).
    if (willSendInterviewEmail && busyConflicts.length && !busyAcknowledged) {
      setBusyConfirm(true);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        status:          statusModal.status,
        rejectionReason: statusModal.status === 'Rejected' ? statusModal.rejectionReason : null,
        holdReason:      statusModal.status === 'Hold'     ? statusModal.holdReason     : null,
        droppedReason:   statusModal.status === 'Dropped'  ? statusModal.droppedReason  : null,
      };
      // Strip the internal _origIdx tag so it never gets persisted to the DB.
      // Each existing round's `id` is kept (every handler spreads {...rr}): the
      // server matches rounds by it, so deleting/renumbering never moves one
      // round's RSVPs, recordings or history onto another. New rounds go
      // without an id and the server assigns one.
      // Rounds are persisted for Interview saves AND whenever a deletion was
      // queued — otherwise switching status away from Interview in the same save
      // would silently discard the deletion (and its cancellation email).
      if (statusModal.status === 'Interview' || toCancel.length) {
        payload.interviewRounds = finalRounds.map(({ _origIdx, ...r }) => r);
      }

      const updRes = await submissionAPI.update(statusModal.submission.id, payload);

      // Email cancellations for any scheduled rounds the recruiter deleted — each
      // removes the event from the candidate's calendar via an iCalendar CANCEL.
      // Not gated on status: a deleted invited round must be cancelled even if the
      // recruiter is simultaneously moving the submission to Rejected/Hold/etc.
      if (statusModal.toEmail && toCancel.length) {
        let cancelOk = 0;
        for (const cr of toCancel) {
          try {
            await emailAPI.cancelInterview({
              candidateId:   statusModal.submission.candidateId,
              requirementId: statusModal.submission.requirementId,
              submissionId:  statusModal.submission.id,
              roundId:       cr.id || undefined,
              calendarUidKey: cr.calendarUidKey || undefined,
              roundNumber:   cr.round || 1,
              roundTitle:    cr.title || '',
              interviewDate:    cr.date,
              interviewTime:    cr.time,
              interviewEndTime: cr.endTime || '',
              interviewMode:    cr.mode || '',
              toEmail:       statusModal.toEmail,
              toName:        statusModal.toName,
              // Cancel notifies the interviewers who were INVITED to this round
              // (stored on it at invite time); fall back to the current chip list
              // for rounds invited before the list was being stored.
              interviewerEmails: (cr.interviewerEmails && cr.interviewerEmails.length) ? cr.interviewerEmails : interviewerList,
            });
            cancelOk += 1;
          } catch (err) {
            if (!emailOutcomeUnknown(err)) {
              toast.error(`Round deleted, but cancellation email failed: ${err.response.data?.message || 'please try again.'}`);
            } else {
              toast('Round deleted. The cancellation email is still sending — check Email Logs before resending.', { icon: '⏳', duration: 8000 });
            }
          }
        }
        if (cancelOk > 0) toast.success(`Interview ${cancelOk > 1 ? 'rounds' : 'round'} cancelled — candidate notified.`);
      }

      // Send interview email for the latest scheduled round
      if (willSendInterviewEmail) {
        // Stable id of the round being emailed. A round added in this save has
        // none yet — read the id the server just assigned from the saved copy
        // (same array order as sent). A legacy round that predates ids is sent
        // without one so the server keeps its round-number calendar UID.
        const savedRounds = updRes?.data?.submission?.interviewRounds;
        const sendIdx = explicitSend ? explicitIdx : finalRounds.length - 1;
        const isNewRound = latestRound._origIdx == null;
        const roundId = latestRound.id
          || (isNewRound && Array.isArray(savedRounds) && savedRounds.length === finalRounds.length
            ? savedRounds[sendIdx]?.id : undefined);
        try {
          const sendRes = await emailAPI.sendInterview({
            candidateId:   statusModal.submission.candidateId,
            requirementId: statusModal.submission.requirementId,
            submissionId:  statusModal.submission.id,   // lets the backend mint/reuse a Teams join link on this round
            isReschedule,
            previousDate:    isReschedule ? prevSlot.date : '',
            previousTime:    isReschedule ? (prevSlot.time    || '') : '',
            previousEndTime: isReschedule ? (prevSlot.endTime || '') : '',
            toEmail:       statusModal.toEmail,
            toName:        statusModal.toName,
            interviewerEmails: interviewerList,
            customMessage: statusModal.customMessage,
            attachResume:  statusModal.attachResume,
            interviewDate:    latestRound.date,
            interviewTime:    latestRound.time,
            interviewEndTime: latestRound.endTime || '',
            interviewMode:    latestRound.mode,
            // Names the calendar event + keeps its UID stable per round so a
            // reschedule UPDATES the invite on the candidate's calendar.
            roundTitle:    latestRound.title || '',
            roundId:       roundId || undefined,
            roundNumber:   latestRound.round || finalRounds.length,
          });
          // The endpoint reports per-recipient delivery. Surface it: a silent
          // "sent!" while an interviewer's copy bounced is exactly how a missing
          // invite goes unnoticed until the interview doesn't happen.
          const results  = Array.isArray(sendRes?.data?.results) ? sendRes.data.results : [];
          const failedTo = results.filter(r => r.status === 'failed').map(r => r.to);
          const ivrOk    = results.filter(r => r.type === 'interviewer' && r.status === 'sent');
          const recOk    = results.find(r => r.type === 'recruiter' && r.status === 'sent');
          if (failedTo.length) {
            toast.error(`Status updated, but the interview email failed for: ${failedTo.join(', ')}`, { duration: 9000 });
          } else {
            toast.success(
              `Status updated & interview email sent — candidate${ivrOk.length ? ` + ${ivrOk.length} interviewer${ivrOk.length > 1 ? 's' : ''}` : ''}${recOk ? ` + recruiter (${recOk.name || recOk.to})` : ''}!`
            );
          }
        } catch (err) {
          // No response / gateway timeout = the send may well have completed
          // server-side, so never tell the recruiter to simply retry — that
          // would deliver a duplicate invite.
          if (!emailOutcomeUnknown(err)) {
            toast.error(`Status updated, but interview email failed: ${err.response.data?.message || 'please try again.'}`);
          } else {
            toast('Status updated. The interview email is still sending — check Email Logs before resending.', { icon: '⏳', duration: 8000 });
          }
        }
      } else {
        toast.success('Status updated!');
      }
      setStatusModal(BLANK_STATUS_MODAL);
      load(page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update status.');
    } finally { setSaving(false); }
  };

  // When a requirement is selected, auto-derive clientId from it
  const handleNewRequirementChange = (requirementId) => {
    const req = requirements.find(r => r.id === requirementId);
    setNewForm(f => ({ ...f, requirementId, clientId: req?.clientId || '' }));
  };

  const handleCreate = async () => {
    if (!newForm.candidateId)   return toast.error('Please select a candidate.');
    if (!newForm.requirementId) return toast.error('Please select a requirement.');
    // No client-side refusal here: the candidate list is loaded once and may be
    // older than a resume uploaded since. The server checks the live record and
    // its RESUME_REQUIRED message is shown by the catch below.
    setSaving(true);
    try {
      await submissionAPI.create(newForm);
      toast.success('Submission created!');
      setNewModal(false);
      setNewForm(BLANK_NEW);
      load(1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create submission.');
    } finally { setSaving(false); }
  };

  const handleSaveBilling = async () => {
    if (!editBilling) return;
    setSaving(true);
    try {
      await submissionAPI.update(editBilling.id, {
        overrideBillingCost:     editBilling.overrideBillingCost     !== '' ? editBilling.overrideBillingCost     : null,
        overrideBillingCurrency: editBilling.overrideBillingCurrency || null,
        overrideBillingType:     editBilling.overrideBillingType     || null,
        notes:                   editBilling.notes                   || null,
      });
      toast.success('Submission updated!');
      setEditBilling(null);
      load(page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update submission.');
    } finally { setSaving(false); }
  };

  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleDelete = (id) => { setConfirmDelete(id); };

  const handleDeleteConfirmed = async () => {
    const id = confirmDelete;
    setConfirmDelete(null);
    try {
      await submissionAPI.delete(id);
      toast.success('Submission deleted.');
      load(page);
    } catch { toast.error('Failed to delete.'); }
  };

  /* Lock body scroll whenever any overlay is open */
  const anyOverlayOpen = statusModal.open || !!newModal || !!editBilling || !!confirmDelete || historyPanel.open || globalHistory;
  useLayoutEffect(() => {
    if (anyOverlayOpen) {
      document.documentElement.classList.add('scroll-locked');
      return () => { document.documentElement.classList.remove('scroll-locked'); };
    }
  }, [anyOverlayOpen]);

  const cardBg     = isDark ? 'var(--bg-card)'   : '#ffffff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#e8ecf1';

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>Submissions</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
            {total} total · Track candidate submissions across all clients and requirements.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setGlobalHistory(true)}>🕐 History</Button>
          {canCreate && <Button onClick={() => setNewModal(true)}>+ New Submission</Button>}
        </div>
      </div>

      <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-center">
        <Input placeholder="Search candidate name…" value={search}
          onChange={e => setSearch(e.target.value)} className="w-full sm:max-w-xs" />
        <Select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} className="w-full sm:w-40">
          <option value="">All Status</option>
          {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
        </Select>
        <SearchableSelect
          value={filters.clientId}
          onChange={val => setFilters({ ...filters, clientId: val, requirementId: '' })}
          placeholder="All Clients"
          className="w-full sm:w-48"
          options={clients.map(c => ({ value: c.id, label: c.clientName }))}
        />
        {/* Requirement Status filter — cascades into the Requirement
            dropdown. Selecting a status clears the picked requirement so
            users don't end up with a stale selection that no longer matches. */}
        <SearchableSelect
          value={filters.requirementStatus}
          onChange={val => setFilters({ ...filters, requirementStatus: val, requirementId: '' })}
          placeholder="All Req Statuses"
          className="w-full sm:w-44"
          options={[
            { value: 'Open',    label: 'Open'    },
            { value: 'On Hold', label: 'On Hold' },
            { value: 'Closed',  label: 'Closed'  },
          ]}
        />
        {/* Requirement filter — cascade chain: client → status → requirement. */}
        <SearchableSelect
          value={filters.requirementId}
          onChange={val => setFilters({ ...filters, requirementId: val })}
          placeholder="All Requirements"
          className="w-full sm:w-52"
          options={requirements
            .filter(r => !filters.clientId          || (r.clientId || r.client?.id) === filters.clientId)
            .filter(r => !filters.requirementStatus || r.status === filters.requirementStatus)
            .map(r => ({ value: r.id, label: r.jobTitle, sub: r.client?.clientName || '' }))}
        />
        {/* Recruiter filter — who submitted. Options come from the list response
            itself, so the dropdown holds exactly the names visible on the page. */}
        {canFilterByRecruiter && authors.length > 0 && (
          <SearchableSelect
            value={filters.recruiterId}
            onChange={val => setFilters({ ...filters, recruiterId: val })}
            placeholder="All Recruiters"
            className="w-full sm:w-52"
            options={authorOptions(authors)}
          />
        )}
        {/* Submitted-on range. Both bounds inclusive; either may be used alone.
            Nothing can be submitted in the future, so both pickers are capped at
            today — future dates would only ever return an empty list. Computed
            per render so the cap rolls over correctly if a tab is left open
            across midnight. */}
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <label style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Submitted</label>
          {/* max is the EARLIER of the chosen end date and today — using
              `createdTo || today` alone would let a future end date (from an
              older session or a hand-edited value) unlock future days here. */}
          <Input type="date" aria-label="Submitted from" value={filters.createdFrom}
            max={[filters.createdTo, todayLocal()].filter(Boolean).sort()[0]}
            onChange={e => setFilters({ ...filters, createdFrom: e.target.value })}
            className="flex-1 min-w-0 sm:flex-none sm:w-40" />
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>→</span>
          <Input type="date" aria-label="Submitted to" value={filters.createdTo}
            min={filters.createdFrom || undefined}
            max={todayLocal()}
            onChange={e => setFilters({ ...filters, createdTo: e.target.value })}
            className="flex-1 min-w-0 sm:flex-none sm:w-40" />
        </div>
        {/* Oldest-first answers "which came in first" in one click. */}
        <Select value={filters.sortOrder}
          onChange={e => setFilters({ ...filters, sortOrder: e.target.value })}
          className="w-full sm:w-44">
          <option value="DESC">Newest first</option>
          <option value="ASC">Oldest first</option>
        </Select>
        <div className="w-full sm:w-auto flex flex-wrap gap-2">
          <Button type="submit" variant="primary" size="sm">Search</Button>
          {(search || filters.status || filters.clientId || filters.requirementId || filters.requirementStatus
            || filters.recruiterId || filters.createdFrom || filters.createdTo || filters.sortOrder !== 'DESC') && (
            <Button type="button" variant="ghost" size="sm"
              onClick={() => { setSearch(''); setFilters({ status: '', clientId: '', requirementId: '', requirementStatus: '', recruiterId: '', createdFrom: '', createdTo: '', sortOrder: 'DESC' }); }}>
              Clear filters
            </Button>
          )}
        </div>
      </form>

      {loading ? <div className="py-16 flex justify-center"><Spinner /></div> : submissions.length === 0 ? (
        <EmptyState icon="📤" title="No submissions found"
          message="Create a submission to start tracking a candidate's progress with a client."
          action={canCreate && <Button onClick={() => setNewModal(true)}>+ New Submission</Button>} />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {submissions.map(sub => (
            <div key={sub.id} style={{
              background: cardBg, border: `1px solid ${cardBorder}`,
              borderRadius: 14, padding: 18,
              display: 'flex', flexDirection: 'column', gap: 10,
              transition: 'box-shadow 0.15s, border-color 0.15s',
              boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.04)',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = isDark ? 'rgba(99,102,241,0.35)' : '#a5b4fc'; e.currentTarget.style.boxShadow = isDark ? '0 4px 20px rgba(0,0,0,0.25)' : '0 4px 16px rgba(99,102,241,0.1)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = cardBorder; e.currentTarget.style.boxShadow = isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.04)'; }}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div style={{
                    width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                    background: 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 700, fontSize: 14,
                    boxShadow: '0 2px 8px rgba(99,102,241,0.3)',
                  }}>
                    {sub.candidate?.fullName?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <button
                      onClick={() => navigate(`/candidate-profile/${sub.candidateId}`, { state: { from: '/submissions', backLabel: 'Back to Submissions' } })}
                      className="truncate block w-full"
                      style={{ fontWeight: 600, fontSize: 13, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', color: 'var(--text-primary)' }}
                    >
                      {sub.candidate?.fullName}
                    </button>
                    <p className="break-all" style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 1, overflowWrap: 'anywhere', wordBreak: 'break-all' }}>{sub.candidate?.email}</p>
                  </div>
                </div>
                <div style={{ flexShrink: 0 }}>
                  <Badge variant={STATUS_VARIANT[sub.status]}>{sub.status}</Badge>
                </div>
              </div>

              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }} className="space-y-1">
                <p className="truncate">🏢 {sub.client?.clientName || '—'}</p>
                {sub.requirement && <p className="truncate">📋 {sub.requirement.jobTitle}</p>}
                {sub.requirement?.shiftTime && <p className="truncate">🕐 Shift: {sub.requirement.shiftTime}</p>}
                {sub.candidate?.currentRole && <p className="truncate">💼 {fmtExp(sub.candidate.experience) || '0 yrs'} · {sub.candidate.currentRole}{sub.candidate.currentCompany ? ` @ ${sub.candidate.currentCompany}` : ''}</p>}
                {!isDeveloper && sub.overrideBillingCost != null && (
                  <p>⚡ Bill Rate: <span style={{ fontWeight: 600, color: isDark ? '#34d399' : '#059669' }}>
                    {sub.overrideBillingCurrency || 'USD'} {sub.overrideBillingCost}
                    {sub.overrideBillingType ? ` / ${sub.overrideBillingType}` : ''}
                  </span></p>
                )}

                {/* ── Extra candidate details ── */}
                {sub.candidate?.phone && (
                  <p>📞 <a href={`tel:${sub.candidate.phone}`} style={{ color: 'var(--text-secondary)' }} onClick={e => e.stopPropagation()}>{sub.candidate.phone}</a></p>
                )}
                {!isDeveloper && (() => {
                  const cur = fmtLPA(sub.candidate?.currentSalary);
                  const exp = fmtLPA(sub.candidate?.expectedSalary);
                  if (!cur && !exp) return null;
                  return (
                    <p>💰 CTC: {cur || '—'} <span style={{ opacity: 0.6 }}>→</span> <span style={{ fontWeight: 600, color: isDark ? '#fbbf24' : '#b45309' }}>{exp || '—'}</span></p>
                  );
                })()}
                {sub.candidate?.noticePeriod != null && sub.candidate.noticePeriod !== '' && (
                  <p>⏱ Notice: {sub.candidate.noticePeriod}d{sub.candidate.servingNoticePeriod ? ' · serving' : ''}</p>
                )}
                {sub.candidate?.location && <p className="truncate">📍 {sub.candidate.location}</p>}
                {/* Submitted-on stamp — full date+time (two submissions on the
                    same day is normal; the time is what says which came first),
                    with a relative age for quick scanning. */}
                {sub.createdAt && (
                  <p className="truncate" title={new Date(sub.createdAt).toString()}>
                    🗓 Submitted{' '}
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                      {formatDateTime(sub.createdAt)}
                    </span>
                    <span style={{ color: 'var(--text-muted)' }}> · {timeAgo(sub.createdAt)}</span>
                  </p>
                )}
                {sub.createdByUser?.name && (
                  <p className="truncate">👤 By {sub.createdByUser.name}</p>
                )}
              </div>

              {sub.candidate?.skills?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {sub.candidate.skills.slice(0, 4).map(s => (
                    <span key={s} style={{
                      fontSize: 10, padding: '2px 7px', borderRadius: 99,
                      background: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff',
                      color: isDark ? '#a5b4fc' : '#4f46e5',
                      border: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : '#c7d2fe'}`,
                    }}>{s}</span>
                  ))}
                </div>
              )}

              {sub.status === 'Interview' && (() => {
                const rounds = Array.isArray(sub.interviewRounds) && sub.interviewRounds.length > 0
                  ? sub.interviewRounds
                  : (sub.interviewDate ? [{ round: 1, date: sub.interviewDate, time: sub.interviewTime, mode: sub.interviewMode, result: 'Pending' }] : []);
                if (rounds.length === 0) return null;
                const resultColor = (r) => r === 'Pass' ? (isDark ? '#6ee7b7' : '#047857') : r === 'Fail' ? (isDark ? '#fca5a5' : '#b91c1c') : (isDark ? '#fcd34d' : '#b45309');
                const resultBg    = (r) => r === 'Pass' ? (isDark ? 'rgba(16,185,129,0.15)' : '#ecfdf5') : r === 'Fail' ? (isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2') : (isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb');
                return (
                  <div style={{ borderRadius: 8, border: `1px solid ${isDark ? 'rgba(99,102,241,0.18)' : '#c7d2fe'}`, background: isDark ? 'rgba(99,102,241,0.06)' : '#eef2ff', padding: '8px 12px' }} className="space-y-2">
                    {rounds.map((r, i) => (
                      <div
                        key={i}
                        role="button"
                        tabIndex={0}
                        aria-label={`Round ${r.round || i + 1} details`}
                        onClick={() => setRoundModal({ round: r, sub })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setRoundModal({ round: r, sub }); }
                        }}
                        style={{ fontSize: 11, cursor: 'pointer', borderRadius: 6, margin: '0 -4px', padding: '2px 4px' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = isDark ? 'rgba(99,102,241,0.10)' : 'rgba(99,102,241,0.07)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, color: isDark ? '#a5b4fc' : '#6366f1', minWidth: 20 }}>R{r.round || i + 1}</span>
                          {r.title && <span style={{ fontWeight: 600, color: 'var(--text-primary)', wordBreak: 'break-word' }}>{r.title}</span>}
                          <span style={{ color: isDark ? '#c7d2fe' : '#4f46e5', flex: 1, minWidth: 0, wordBreak: 'break-word' }}>
                            {formatDate(r.date)}{r.time ? ` · ${fmtTimeRange(r.time, r.endTime)}` : ''}{r.mode ? ` · ${r.mode}` : ''}
                          </span>
                          {r.result && (
                            <span style={{ background: resultBg(r.result), color: resultColor(r.result), border: `1px solid ${resultColor(r.result)}33`, borderRadius: 99, padding: '1px 7px', fontWeight: 600, fontSize: 10 }}>
                              {r.result}
                            </span>
                          )}
                        </div>
                        {/* Candidate's RSVP to the invite for this round. Only
                            shown once an invite went out — before that there is
                            nothing for them to have answered. */}
                        <div style={{ paddingLeft: 26, marginTop: 3 }}>
                          <RoundRsvp round={r} />
                        </div>
                        {r.resultDesc && (
                          <SafeHtml html={r.resultDesc}
                            style={{ fontSize: 10, color: 'var(--text-muted)', paddingLeft: 26, marginTop: 2, fontStyle: 'italic' }} />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {sub.status === 'Rejected' && sub.rejectionReason && (
                <div style={{ fontSize: 11, color: isDark ? '#fca5a5' : '#dc2626', fontStyle: 'italic', display: 'flex', gap: 4 }}>
                  <span>⛔</span>
                  <SafeHtml html={sub.rejectionReason} />
                </div>
              )}

              {sub.status === 'Hold' && sub.holdReason && (
                <div style={{ fontSize: 11, color: isDark ? '#fcd34d' : '#b45309', fontStyle: 'italic', display: 'flex', gap: 4 }}>
                  <span>⏸</span>
                  <SafeHtml html={sub.holdReason} />
                </div>
              )}

              {sub.status === 'Dropped' && sub.droppedReason && (
                <div style={{ fontSize: 11, color: isDark ? '#a5b4fc' : '#4338ca', fontStyle: 'italic', display: 'flex', gap: 4 }}>
                  <span>🚪</span>
                  <SafeHtml html={sub.droppedReason} />
                </div>
              )}

              <div style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f1f5f9'}`, paddingTop: 10 }}
                className="flex gap-2 flex-wrap items-center" onClick={e => e.stopPropagation()}>
                <Button variant="primary" size="xs" onClick={() => openStatus(sub)}>
                  🔄 Update Status
                </Button>
                <Button variant="teal" size="xs"
                  onClick={() => navigate(`/candidate-profile/${sub.candidateId}`, { state: { from: '/submissions', backLabel: 'Back to Submissions' } })}>
                  👤 View Profile
                </Button>
                {sub.candidateId && (
                  <Button variant="outline" size="xs" tooltip="Quick candidate details"
                    onClick={() => setDetailsSub(sub)}>
                    📄 Details
                  </Button>
                )}
                <Button variant="outline" size="xs" tooltip="View activity history"
                  onClick={() => setHistoryPanel({ open: true, id: sub.id, name: `${sub.candidate?.fullName || 'Submission'} → ${sub.client?.clientName || ''}` })}>
                  🕐 History
                </Button>
                {hasRole(['SuperAdmin', 'RecruiterManager', 'Developer']) && (
                  <Button variant="success" size="xs"
                    onClick={() => navigate('/emails', {
                      state: {
                        compose:     'client',
                        candidate:   { id: sub.candidateId,   ...sub.candidate },
                        requirement: { id: sub.requirementId, ...sub.requirement },
                        fromSource:  'submission',
                      },
                    })}>
                    ✉️ Email Client
                  </Button>
                )}
                {canEditBilling && (
                  <Button variant="warning" size="xs"
                    onClick={() => setEditBilling({
                      id:                      sub.id,
                      candidateName:           sub.candidate?.fullName || 'Submission',
                      overrideBillingCost:     sub.overrideBillingCost     ?? '',
                      overrideBillingCurrency: sub.overrideBillingCurrency ?? 'USD',
                      overrideBillingType:     sub.overrideBillingType     ?? '',
                      notes:                   sub.notes                   ?? '',
                    })}>
                    ✏️ Edit
                  </Button>
                )}
                {canDelete && (
                  <Button variant="danger" size="xs"
                    onClick={(e) => { e.stopPropagation(); handleDelete(sub.id); }}>
                    Delete
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination page={page} pages={pages} onPage={p => { setPage(p); load(p); }} />

      {/* ── Status Update Modal ── */}
      <Modal isOpen={statusModal.open} onClose={() => setStatusModal(BLANK_STATUS_MODAL)}
        title={`Update Status — ${statusModal.submission?.candidate?.fullName}`} size="lg">
        <div className="space-y-4">
          <StatusPicker
            label="Status"
            value={statusModal.status}
            onChange={s => setStatusModal(m => ({ ...m, status: s }))}
            options={STATUS_OPTIONS}
            variants={STATUS_VARIANT}
          />

          {statusModal.status === 'Rejected' && (
            <RichTextEditor label="Rejection Reason" value={statusModal.rejectionReason} rows={3}
              onChange={(html) => setStatusModal(m => ({ ...m, rejectionReason: html }))}
              placeholder="Why was the candidate rejected?" />
          )}

          {statusModal.status === 'Hold' && (
            <RichTextEditor label="Hold Reason *" value={statusModal.holdReason} rows={3}
              onChange={(html) => setStatusModal(m => ({ ...m, holdReason: html }))}
              placeholder="Why is this submission on hold? (e.g. waiting on client decision, candidate unavailable…)" />
          )}

          {statusModal.status === 'Dropped' && (
            <RichTextEditor label="Drop Reason *" value={statusModal.droppedReason} rows={3}
              onChange={(html) => setStatusModal(m => ({ ...m, droppedReason: html }))}
              placeholder="Why did the candidate drop out? (e.g. accepted another offer, not interested anymore…)" />
          )}

          {statusModal.status === 'Interview' && (() => {
            const rounds       = statusModal.interviewRounds;
            const allDone      = rounds.length > 0 && rounds.every(r => r.result && r.result !== 'Pending');
            const canAddRound  = !statusModal.addingRound && (rounds.length === 0 || allDone);
            // The email card appears only when THIS save would send an invite:
            // a new round being scheduled, or the latest round's slot changed
            // (a reschedule). Mirrors the send condition — plain result/notes
            // edits never show (or send) the interview email.
            const latestR      = rounds[rounds.length - 1];
            const latestSnap   = (statusModal.origSnapshot || []).find(s => s.origIdx === latestR?._origIdx);
            const invitingRound = statusModal.sendInviteOrigIdx != null
              && rounds.some(r => r._origIdx === statusModal.sendInviteOrigIdx);
            const willEmail    = statusModal.addingRound || invitingRound || !!(latestSnap && latestR?.date
              && (latestSnap.date !== latestR.date
                  || (latestSnap.time    || '') !== (latestR.time    || '')
                  || (latestSnap.endTime || '') !== (latestR.endTime || '')));
            const resultColor  = (r) => r === 'Pass' ? (isDark ? '#6ee7b7' : '#047857') : r === 'Fail' ? (isDark ? '#fca5a5' : '#b91c1c') : (isDark ? '#fcd34d' : '#b45309');
            const resultBg     = (r) => r === 'Pass' ? (isDark ? 'rgba(16,185,129,0.15)' : '#ecfdf5') : r === 'Fail' ? (isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2') : (isDark ? 'rgba(245,158,11,0.15)' : '#fffbeb');
            const resultBorder = (r) => r === 'Pass' ? (isDark ? 'rgba(16,185,129,0.3)' : '#a7f3d0') : r === 'Fail' ? (isDark ? 'rgba(239,68,68,0.3)' : '#fecaca') : (isDark ? 'rgba(245,158,11,0.3)' : '#fde68a');
            return (
              <div style={{ borderRadius: 10, border: `1px solid ${isDark ? 'rgba(99,102,241,0.25)' : '#ddd6fe'}`, padding: 16, background: isDark ? 'rgba(99,102,241,0.07)' : '#f5f3ff' }} className="space-y-4">
                <p style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#a5b4fc' : '#6366f1' }}>🎯 Interview Rounds</p>

                {/* ── Existing rounds ── */}
                {rounds.length > 0 && (
                  <div className="space-y-3">
                    {rounds.map((r, idx) => (
                      <div key={idx} style={{ borderRadius: 8, border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e0d9ff'}`, padding: '10px 12px', background: isDark ? 'rgba(0,0,0,0.2)' : '#ffffff' }} className="space-y-2">
                        {/* Header row: round label + date/time + result buttons + delete */}
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div>
                            <span style={{ fontSize: 11, fontWeight: 700, color: isDark ? '#a5b4fc' : '#6366f1' }}>Round {r.round || idx + 1}</span>
                            {r.title && <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', marginLeft: 6 }}>· {r.title}</span>}
                            <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginLeft: 6 }}>
                              {formatDate(r.date)}{r.time ? ` · ${fmtTimeRange(r.time, r.endTime)}` : ''}{r.mode ? ` · ${r.mode}` : ''}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                            {/* Result toggle buttons */}
                            {['Pending', 'Pass', 'Fail'].map(res => (
                              <button key={res} type="button"
                                onClick={() => setStatusModal(m => ({
                                  ...m,
                                  interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, result: res } : rr),
                                }))}
                                style={{
                                  fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 600, cursor: 'pointer', border: '1px solid',
                                  background:  r.result === res ? resultBg(res)     : 'transparent',
                                  color:       r.result === res ? resultColor(res)  : 'var(--text-muted)',
                                  borderColor: r.result === res ? resultBorder(res) : 'var(--border-subtle)',
                                }}>
                                {res}
                              </button>
                            ))}
                            {/* Reschedule — only meaningful while the round is still Pending.
                                Hidden once the interview has been marked Pass or Fail. */}
                            {/* "Forgot to send it" — a round can be scheduled without ever
                                emailing anyone, and until now there was no way to send the
                                invite afterwards: ticking the email box only took effect for a
                                NEW or RESCHEDULED round. Mirrors StatusUpdateModal; this page
                                keeps its own copy of the whole modal. */}
                            {(!r.result || r.result === 'Pending') && r.date && (
                              <button key="send-invite" type="button"
                                title={r.inviteSentAt
                                  ? `Invitation already sent ${new Date(r.inviteSentAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true })} — send it again`
                                  : 'Send the interview invitation for this round'}
                                onClick={() => setStatusModal(m => {
                                  const cur = m.interviewRounds[idx];
                                  if (m.sendInviteOrigIdx === cur?._origIdx) return { ...m, sendInviteOrigIdx: null };
                                  return {
                                    ...m,
                                    sendInviteOrigIdx: cur?._origIdx ?? null,
                                    sendEmail: true,
                                    interviewerEmails: roundInterviewers(cur)
                                      || lastInterviewers(m.interviewRounds)
                                      || m.interviewerEmails,
                                  };
                                })}
                                style={{
                                  fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 600, cursor: 'pointer', border: '1px solid',
                                  background:  statusModal.sendInviteOrigIdx === r._origIdx ? (isDark ? 'rgba(16,185,129,0.18)' : '#d1fae5') : 'transparent',
                                  color:       statusModal.sendInviteOrigIdx === r._origIdx ? (isDark ? '#6ee7b7' : '#047857') : 'var(--text-muted)',
                                  borderColor: statusModal.sendInviteOrigIdx === r._origIdx ? (isDark ? 'rgba(16,185,129,0.4)' : '#a7f3d0') : 'var(--border-subtle)',
                                }}>
                                {r.inviteSentAt ? '✉ Resend invite' : '✉ Send invite'}
                              </button>
                            )}
                            {(!r.result || r.result === 'Pending') && (
                              <button key="reschedule" type="button" title="Reschedule this round"
                                onClick={() => setStatusModal(m => {
                                  const opening = m.reschedulingIdx !== idx;
                                  if (!opening) return { ...m, reschedulingIdx: null };
                                  const cur = m.interviewRounds[idx];
                                  // Rounds scheduled before end times existed have a start but no
                                  // end. Backfill the default slot so the now-required field opens
                                  // pre-filled — and mirror it into origSnapshot so this backfill
                                  // alone isn't mistaken for a reschedule (which would email the
                                  // candidate even though nothing actually moved).
                                  const backfill = !!(cur?.time && !cur?.endTime);
                                  const endTime  = backfill ? (addMinutesToTime(cur.time, DEFAULT_SLOT_MINS) || '') : cur?.endTime;
                                  return {
                                    ...m,
                                    reschedulingIdx: idx,
                                    interviewRounds: backfill
                                      ? m.interviewRounds.map((rr, i) => i === idx ? { ...rr, endTime } : rr)
                                      : m.interviewRounds,
                                    origSnapshot: backfill
                                      ? (m.origSnapshot || []).map(s => s.origIdx === cur._origIdx ? { ...s, endTime } : s)
                                      : m.origSnapshot,
                                    // Re-seed the chips with the interviewers actually invited to
                                    // THAT round, so the recruiter never retypes them. Falls back to
                                    // the last panel used, then to whatever is already typed.
                                    interviewerEmails: roundInterviewers(cur)
                                      || lastInterviewers(m.interviewRounds)
                                      || m.interviewerEmails,
                                  };
                                })}
                                style={{
                                  fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 600, cursor: 'pointer', border: '1px solid',
                                  background:  statusModal.reschedulingIdx === idx ? (isDark ? 'rgba(99,102,241,0.18)' : '#e0e7ff') : 'transparent',
                                  color:       statusModal.reschedulingIdx === idx ? (isDark ? '#a5b4fc' : '#4338ca') : 'var(--text-muted)',
                                  borderColor: statusModal.reschedulingIdx === idx ? (isDark ? 'rgba(99,102,241,0.4)' : '#c7d2fe') : 'var(--border-subtle)',
                                }}>
                                🔁 Reschedule
                              </button>
                            )}
                            {/* Delete round — only while still Pending (upcoming). Once a
                                round is marked Pass/Fail it is a historical record and cannot
                                be deleted. */}
                            {(!r.result || r.result === 'Pending') && (
                            <button type="button" title="Delete this round"
                              onClick={() => {
                                // Deleting an upcoming round always asks for confirmation. If the
                                // candidate was actually INVITED (invite email sent / Teams meeting
                                // minted) the confirm also queues a cancellation email + calendar
                                // removal; otherwise it just removes the round.
                                const removed = statusModal.interviewRounds[idx];
                                const snap = (statusModal.origSnapshot || []).find(s => s.origIdx === removed?._origIdx);
                                const wasInvited = !!(snap && snap.date && (snap.inviteSentAt || snap.meetingLink));
                                setCancelConfirm({
                                  origIdx: removed?._origIdx,
                                  snap: wasInvited ? snap : null,   // queue a cancellation only for invited rounds
                                  when: snap ? [snap.date, snap.time].filter(Boolean).join(' ') : '',
                                  wasInvited,
                                });
                              }}
                              style={{
                                marginLeft: 6, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: 30, height: 30, borderRadius: 8, cursor: 'pointer', transition: 'all 0.12s',
                                color: isDark ? '#fca5a5' : '#dc2626',
                                background: isDark ? 'rgba(239,68,68,0.16)' : 'rgba(239,68,68,0.10)',
                                border: `1px solid ${isDark ? 'rgba(239,68,68,0.40)' : 'rgba(239,68,68,0.30)'}`,
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = '#ef4444'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#ef4444'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = isDark ? 'rgba(239,68,68,0.16)' : 'rgba(239,68,68,0.10)'; e.currentTarget.style.color = isDark ? '#fca5a5' : '#dc2626'; e.currentTarget.style.borderColor = isDark ? 'rgba(239,68,68,0.40)' : 'rgba(239,68,68,0.30)'; }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                            </button>
                            )}
                          </div>
                        </div>
                        {/* Inline reschedule form — opens beneath the round header.
                            Edits the round in place so the round number stays the same. */}
                        {statusModal.reschedulingIdx === idx && (
                          <div style={{ borderTop: `1px dashed ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`, paddingTop: 10, marginTop: 6 }} className="space-y-2">
                            <p style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#a5b4fc' : '#6366f1' }}>
                              🔁 Reschedule Round {r.round || idx + 1}
                            </p>
                            <Input label="Interview Title (optional)"
                              placeholder="e.g. Technical Round, HR Interview…"
                              value={r.title || ''}
                              onChange={e => setStatusModal(m => ({
                                ...m,
                                interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, title: e.target.value } : rr),
                              }))} />
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                              <Input label="Date *" type="date"
                                value={r.date || ''}
                                onChange={e => setStatusModal(m => ({
                                  ...m,
                                  interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, date: e.target.value } : rr),
                                }))} />
                              <TimePicker12 label="Start Time"
                                value={r.time || ''}
                                onChange={v => setStatusModal(m => ({
                                  ...m,
                                  interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? withStartTime(rr, v) : rr),
                                }))} />
                              <TimePicker12 label={r.time ? 'End Time *' : 'End Time'}
                                value={r.endTime || ''}
                                onChange={v => setStatusModal(m => ({
                                  ...m,
                                  interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, endTime: v } : rr),
                                }))} />
                              <Select label="Mode"
                                value={r.mode || ''}
                                onChange={e => setStatusModal(m => ({
                                  ...m,
                                  interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, mode: e.target.value } : rr),
                                }))}>
                                <option value="">— Select —</option>
                                <option>In-Person</option>
                                <option>Online</option>
                                <option>Phone</option>
                              </Select>
                            </div>
                            <RichTextEditor label="Notes (optional)" rows={3}
                              value={r.notes || ''}
                              onChange={(html) => setStatusModal(m => ({
                                ...m,
                                interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, notes: html } : rr),
                              }))} />
                            <div className="flex justify-end">
                              <Button size="xs" variant="primary"
                                onClick={() => setStatusModal(m => ({ ...m, reschedulingIdx: null }))}>
                                Done
                              </Button>
                            </div>
                          </div>
                        )}
                        {r.notes && statusModal.reschedulingIdx !== idx && (
                          <SafeHtml html={r.notes}
                            style={{ fontSize: 11, color: 'var(--text-muted)' }} />
                        )}
                        {/* Result description — editable textarea, shown once a result is chosen */}
                        {r.result && r.result !== 'Pending' && (
                          <div>
                            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 3 }}>
                              Result Notes {r.result === 'Pass' ? '✅' : '❌'}
                            </label>
                            <RichTextEditor
                              rows={3}
                              placeholder={r.result === 'Pass' ? 'What went well? Any feedback…' : 'Reason for rejection or areas of concern…'}
                              value={r.resultDesc || ''}
                              onChange={(html) => setStatusModal(m => ({
                                ...m,
                                interviewRounds: m.interviewRounds.map((rr, i) => i === idx ? { ...rr, resultDesc: html } : rr),
                              }))}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* ── Add next round button ── */}
                {canAddRound && (
                  <button type="button"
                    onClick={() => setStatusModal(m => ({ ...m, addingRound: true, newRound: { ...BLANK_ROUND } }))}
                    style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#a5b4fc' : '#6366f1', background: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff', border: `1px dashed ${isDark ? 'rgba(99,102,241,0.4)' : '#a5b4fc'}`, borderRadius: 8, padding: '8px 14px', cursor: 'pointer', width: '100%' }}>
                    + Schedule Round {rounds.length + 1}
                  </button>
                )}

                {/* ── New round form ── */}
                {statusModal.addingRound && (
                  <div style={{ borderRadius: 8, border: `1px solid ${isDark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}`, padding: 12, background: isDark ? 'rgba(99,102,241,0.06)' : '#f5f3ff' }} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#a5b4fc' : '#6366f1' }}>
                        📅 Schedule Round {rounds.length + 1}
                      </p>
                      {rounds.length > 0 && (
                        <button type="button" onClick={() => setStatusModal(m => ({ ...m, addingRound: false }))}
                          style={{ fontSize: 18, lineHeight: 1, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
                      )}
                    </div>
                    <Input label="Interview Title (optional)"
                      placeholder="e.g. Technical Round, HR Interview, Manager Round…"
                      value={statusModal.newRound.title}
                      onChange={e => setStatusModal(m => ({ ...m, newRound: { ...m.newRound, title: e.target.value } }))} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                      <Input label="Date *" type="date"
                        value={statusModal.newRound.date}
                        onChange={e => setStatusModal(m => ({ ...m, newRound: { ...m.newRound, date: e.target.value } }))} />
                      <TimePicker12 label="Start Time"
                        value={statusModal.newRound.time}
                        onChange={v => setStatusModal(m => ({ ...m, newRound: withStartTime(m.newRound, v) }))} />
                      <TimePicker12 label={statusModal.newRound.time ? 'End Time *' : 'End Time'}
                        value={statusModal.newRound.endTime}
                        onChange={v => setStatusModal(m => ({ ...m, newRound: { ...m.newRound, endTime: v } }))} />
                      <Select label="Mode"
                        value={statusModal.newRound.mode}
                        onChange={e => setStatusModal(m => ({ ...m, newRound: { ...m.newRound, mode: e.target.value } }))}>
                        <option value="">— Select —</option>
                        {MODE_OPTIONS.map(mo => <option key={mo}>{mo}</option>)}
                      </Select>
                    </div>
                    <RichTextEditor label="Notes (optional)" rows={3}
                      value={statusModal.newRound.notes}
                      onChange={(html) => setStatusModal(m => ({ ...m, newRound: { ...m.newRound, notes: html } }))} />
                  </div>
                )}

                {/* ── Send interview email — appears only when this save would send
                    an invite (requirement toggle ON + new round or reschedule) ── */}
                {willEmail && (
                  <div style={{ borderTop: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : '#ddd6fe'}`, paddingTop: 10 }}>
                    <label className="flex items-center gap-2" style={{ cursor: 'pointer' }}>
                      <input type="checkbox" checked={statusModal.sendEmail}
                        onChange={e => requestSendEmailToggle(e.target.checked)}
                        style={{ cursor: 'pointer' }}
                        className="w-4 h-4 rounded accent-indigo-500" />
                      <span style={{ fontSize: 13, color: isDark ? '#a5b4fc' : '#4f46e5', fontWeight: 500 }}>
                        📧 Send interview invitation email to candidate <span style={{ fontWeight: 400, opacity: 0.75 }}>(includes 📅 calendar invite)</span>
                      </span>
                      {lockEmail && (
                        <span style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#fbbf24' : '#b45309', background: isDark ? 'rgba(251,191,36,0.12)' : '#fef3c7', borderRadius: 6, padding: '1px 7px' }}>🔒 Required by requirement</span>
                      )}
                    </label>
                    {statusModal.sendEmail && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                        <Input label="Candidate Email" value={statusModal.toEmail} disabled={lockEmail}
                          title={lockEmail ? 'Locked by requirement setting' : undefined}
                          onChange={e => setStatusModal(m => ({ ...m, toEmail: e.target.value }))} />
                        <Input label="Candidate Name" value={statusModal.toName} disabled={lockEmail}
                          title={lockEmail ? 'Locked by requirement setting' : undefined}
                          onChange={e => setStatusModal(m => ({ ...m, toName: e.target.value }))} />
                        <div className="col-span-2">
                          <EmailTagInput label="Interviewer Email(s)" required isDark={isDark}
                            suggestions={interviewerSuggestions}
                            value={statusModal.interviewerEmails}
                            onChange={v => setStatusModal(m => ({ ...m, interviewerEmails: v }))}
                            placeholder="interviewer@company.com — press Enter to add more" />
                          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                            The interviewer(s) receive the same interview email and calendar invite as the candidate.
                          </p>
                          <InterviewerBusyWarning isDark={isDark}
                            emails={statusModal.interviewerEmails}
                            date={busyTarget?.date} time={busyTarget?.time} endTime={busyTarget?.endTime}
                            submissionId={statusModal.submission?.id} roundId={busyTarget?.id} roundNumber={busyTarget?.round}
                            onChange={setBusyConflicts} />
                        </div>
                        {/* Optional per-send note. Goes to everyone who receives
                            this invitation, so it reads as part of the invite. */}
                        <div className="col-span-2">
                          <RichTextEditor label="Custom message (optional)" rows={4}
                            value={statusModal.customMessage}
                            onChange={(html) => setStatusModal(m => ({ ...m, customMessage: html }))}
                            placeholder="Anything to add for this interview — e.g. what to prepare, who they'll meet, documents to carry…" />
                          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                            Added to this email only — the candidate, interviewer(s) and co-owners all see it.
                            Templates can place it with <code>{'{{custom_message}}'}</code>; otherwise it appears
                            after the invitation details.
                          </p>
                        </div>
                        <label className="col-span-2 flex items-center gap-2 mt-1"
                          style={{
                            cursor: lockEmail ? 'not-allowed' : 'pointer',
                            background: statusModal.attachResume ? (isDark ? 'rgba(16,185,129,0.08)' : '#f0fdf4') : 'var(--bg-raised)',
                            border: `1px solid ${statusModal.attachResume ? (isDark ? 'rgba(16,185,129,0.3)' : '#a7f3d0') : 'var(--border-subtle)'}`,
                            borderRadius: 8, padding: '8px 12px',
                          }}>
                          <input type="checkbox" checked={statusModal.attachResume} disabled={lockEmail}
                            onChange={e => setStatusModal(m => ({ ...m, attachResume: e.target.checked }))}
                            className="w-4 h-4 accent-emerald-500" style={{ cursor: lockEmail ? 'not-allowed' : 'pointer' }} />
                          <span style={{ fontSize: 13, fontWeight: 500, color: statusModal.attachResume ? (isDark ? '#6ee7b7' : '#047857') : 'var(--text-secondary)' }}>
                            📎 Attach candidate's resume (PDF)
                          </span>
                          {/* Ticked by DEFAULT and lockable by the requirement, so
                              without these the invite could go out with nothing
                              attached and no sign of it. Mirrors StatusUpdateModal;
                              this page keeps its own copy of the whole modal. */}
                          {statusModal.attachResume && !statusModal.submission?.candidate?.resumeName && (
                            <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, color: isDark ? '#f87171' : '#dc2626' }}>
                              ⚠ No resume on file — nothing will be attached
                            </span>
                          )}
                          {statusModal.attachResume && statusModal.submission?.candidate?.resumeName && (
                            <span
                              onClick={e => e.preventDefault()}
                              style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}
                            >
                              <span style={{ fontSize: 11, color: 'var(--text-muted)', overflowWrap: 'anywhere' }}>
                                {statusModal.submission.candidate.resumeName}
                              </span>
                              <ResumeButton
                                variant="link"
                                candidateId={statusModal.submission.candidate.id}
                                resumeName={statusModal.submission.candidate.resumeName}
                                label="View"
                              />
                            </span>
                          )}
                          {lockEmail && (
                            <span style={{ fontSize: 11, fontWeight: 600, color: isDark ? '#fbbf24' : '#b45309', background: isDark ? 'rgba(251,191,36,0.12)' : '#fef3c7', borderRadius: 6, padding: '1px 7px' }}>🔒</span>
                          )}
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setStatusModal(BLANK_STATUS_MODAL)}>Cancel</Button>
            <Button onClick={handleStatusSave} loading={saving}>Save</Button>
          </div>
        </div>
      </Modal>

      {/* ── New Submission Modal ── */}
      <Modal isOpen={newModal} onClose={() => { setNewModal(false); setNewForm(BLANK_NEW); }}
        title="New Submission" size="md">
        <div className="space-y-4">
          <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Select a candidate and the requirement you're submitting them for.
          </p>
          <SearchableSelect
            label="Candidate *"
            required
            value={newForm.candidateId}
            onChange={val => setNewForm(f => ({ ...f, candidateId: val }))}
            placeholder="— Search candidate —"
            options={candidates.map(c => ({
              value: c.id,
              label: c.fullName,
              sub:   c.resumeName ? c.email : `${c.email || ''} · ⚠ no resume — can't be submitted`,
            }))}
          />
          <SearchableSelect
            label="Requirement *"
            required
            value={newForm.requirementId}
            onChange={handleNewRequirementChange}
            placeholder="— Select requirement —"
            options={requirements.map(r => ({
              value: r.id,
              label: r.jobTitle,
              sub:   r.client?.clientName || '',
            }))}
          />

          {/* Override Billing — hidden for Developer role */}
          {!isDeveloper && (
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Override Billing Rate <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                <Select
                  value={newForm.overrideBillingCurrency}
                  onChange={e => setNewForm(f => ({ ...f, overrideBillingCurrency: e.target.value }))}
                  className="w-full sm:w-24"
                >
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </Select>
                <Input
                  type="number"
                  placeholder="Amount"
                  value={newForm.overrideBillingCost}
                  onChange={e => setNewForm(f => ({ ...f, overrideBillingCost: e.target.value }))}
                  className="w-full sm:flex-1 min-w-0"
                />
                <Select
                  value={newForm.overrideBillingType}
                  onChange={e => setNewForm(f => ({ ...f, overrideBillingType: e.target.value }))}
                  className="w-full sm:w-32"
                >
                  <option value="">Type</option>
                  {BILLING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </Select>
              </div>
            </div>
          )}

          <RichTextEditor
            label="Client Notes"
            value={newForm.notes}
            rows={4}
            onChange={(html) => setNewForm(f => ({ ...f, notes: html }))}
            placeholder="Visible to client in batch email…"
          />

          <div className="flex gap-3 justify-end pt-2">
            <Button variant="secondary" onClick={() => { setNewModal(false); setNewForm(BLANK_NEW); }}>Cancel</Button>
            <Button onClick={handleCreate} loading={saving}>Create Submission</Button>
          </div>
        </div>
      </Modal>

      {/* ── Edit Billing Modal (SuperAdmin only) ── */}
      <Modal
        isOpen={!!editBilling}
        onClose={() => setEditBilling(null)}
        title={`Edit Submission — ${editBilling?.candidateName || ''}`}
        size="sm"
      >
        {editBilling && (
          <div className="space-y-4">
            <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Update billing rate and client notes for this submission.
            </p>

            {/* Billing Rate */}
            <div>
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Override Billing Rate
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                <Select
                  value={editBilling.overrideBillingCurrency}
                  onChange={e => setEditBilling(b => ({ ...b, overrideBillingCurrency: e.target.value }))}
                  className="w-full sm:w-24"
                >
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </Select>
                <Input
                  type="number"
                  placeholder="Amount"
                  value={editBilling.overrideBillingCost}
                  onChange={e => setEditBilling(b => ({ ...b, overrideBillingCost: e.target.value }))}
                  className="w-full sm:flex-1 min-w-0"
                />
                <Select
                  value={editBilling.overrideBillingType}
                  onChange={e => setEditBilling(b => ({ ...b, overrideBillingType: e.target.value }))}
                  className="w-full sm:w-32"
                >
                  <option value="">Type</option>
                  {BILLING_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </Select>
              </div>
              {editBilling.overrideBillingCost && (
                <p style={{ fontSize: 11, color: isDark ? '#34d399' : '#059669', marginTop: 4 }}>
                  ⚡ {editBilling.overrideBillingCurrency} {Number(editBilling.overrideBillingCost).toLocaleString()}{editBilling.overrideBillingType ? ` / ${editBilling.overrideBillingType}` : ''}
                </p>
              )}
            </div>

            {/* Client Notes */}
            <RichTextEditor
              label="Client Notes"
              value={editBilling.notes}
              onChange={(html) => setEditBilling(b => ({ ...b, notes: html }))}
              placeholder="Visible to client in batch email…"
              rows={4}
            />

            <div className="flex gap-3 justify-end pt-2">
              <Button variant="secondary" onClick={() => setEditBilling(null)}>Cancel</Button>
              <Button onClick={handleSaveBilling} loading={saving}>Save Changes</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Per-entity History Panel ── */}
      {historyPanel.open && (
        <HistoryPanel
          entityId={historyPanel.id}
          entityName={historyPanel.name}
          onClose={() => setHistoryPanel({ open: false, id: null, name: '' })}
        />
      )}

      {/* ── Global History Feed ── */}
      {globalHistory && (
        <GlobalHistoryPanel
          module="Submission"
          onClose={() => setGlobalHistory(false)}
        />
      )}

      {/* ── Candidate details popup (same component as the Interviews tile) ── */}
      {detailsSub && (
        <CandidateDetailsModal
          candidateId={detailsSub.candidateId}
          candidateFallback={detailsSub.candidate}
          onClose={() => setDetailsSub(null)}
        />
      )}

      {/* ── Interviewer already booked in this slot ── */}
      <ConfirmModal
        isOpen={busyConfirm}
        danger
        title="Interviewer already busy at this time"
        confirmLabel="Send anyway"
        message={`${[...new Set(busyConflicts.map(c => c.email))].join(', ')} already ${busyConflicts.length > 1 ? 'have' : 'has'} an interview in this slot. Sending will double-book them — pick another time or interviewer unless this is intended.`}
        onCancel={() => setBusyConfirm(false)}
        onConfirm={() => { setBusyConfirm(false); busyAckRef.current = true; handleStatusSave(); }}
      />

      {/* ── Confirm cancelling a scheduled interview round ── */}
      <ConfirmModal
        isOpen={!!cancelConfirm}
        danger
        title={cancelConfirm?.wasInvited ? 'Cancel this interview round?' : 'Delete this interview round?'}
        confirmLabel={cancelConfirm?.wasInvited ? 'Yes, cancel it' : 'Delete round'}
        message={cancelConfirm?.wasInvited
          ? `The candidate will be emailed that their interview${cancelConfirm?.when ? ` scheduled for ${cancelConfirm.when}` : ''} is cancelled, and it will be removed from their calendar when you click Save.`
          : `This interview round${cancelConfirm?.when ? ` (${cancelConfirm.when})` : ''} will be removed when you click Save.`}
        onCancel={() => setCancelConfirm(null)}
        onConfirm={() => { if (cancelConfirm) removeRoundByOrigIdx(cancelConfirm.origIdx, cancelConfirm.snap); setCancelConfirm(null); }}
      />

      {/* ── Confirm turning the interview invitation email on/off ── */}
      <ConfirmModal
        isOpen={!!emailConfirm}
        danger={emailConfirm?.next === false}
        title={emailConfirm?.next
          ? 'Send the interview invitation email?'
          : 'Skip the interview invitation email?'}
        confirmLabel={emailConfirm?.next ? 'Yes, send it' : 'Yes, skip it'}
        message={emailConfirm?.next
          ? 'When you click Save, the candidate and every interviewer you list will be emailed this interview invitation, each with a calendar invite.'
          : (lockEmail
              ? 'This requirement is set to always send the interview invitation. If you skip it, the candidate will NOT be told about this interview and will get no calendar invite.'
              : 'The candidate will not be emailed about this interview.')}
        onCancel={() => setEmailConfirm(null)}
        onConfirm={() => {
          setStatusModal(m => ({ ...m, sendEmail: !!emailConfirm?.next }));
          setEmailConfirm(null);
        }}
      />
            {confirmDelete && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)' }} onClick={() => setConfirmDelete(null)} />
          <div style={{ position: 'relative', background: isDark ? '#1e2535' : '#ffffff', borderRadius: 16, padding: '28px', width: '100%', maxWidth: 400, boxShadow: '0 24px 64px rgba(0,0,0,0.4)', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 700, color: isDark ? '#f1f5f9' : '#111827' }}>Delete Submission?</h3>
            <p style={{ margin: '0 0 24px', fontSize: 14, color: isDark ? '#94a3b8' : '#6b7280' }}>This will permanently remove this submission.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmDelete(null)} style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #d1d5db', background: 'transparent', color: isDark ? '#f1f5f9' : '#374151', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleDeleteConfirmed} style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: '#ef4444', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      <InterviewRoundModal
        isOpen={!!roundModal}
        onClose={() => setRoundModal(null)}
        round={roundModal?.round}
        context={{
          candidateName:    roundModal?.sub?.candidate?.fullName,
          candidateEmail:   roundModal?.sub?.candidate?.email,
          clientName:       roundModal?.sub?.client?.clientName,
          requirementTitle: roundModal?.sub?.requirement?.jobTitle,
        }}
      />
    </div>
  );
}